import { colorsPalettes } from "@similarweb/styles";
import { LegendWithValue } from "@similarweb/ui-components";

import { CHART_COLORS } from "app/constants/ChartColors";
import { BasicDurations } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import {
    isDurationSwitcherDisabled,
    isDurationSwitcherLocked,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import type { IBaseMetricItem, ISwitcherItem } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { MetricNames } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";

export const GRANULARITY_SWITCHER_OPTIONS: ISwitcherItem[] = [
    {
        title: "D",
        value: GranularityNames.DAILY,
        disabled: false,
    },
    {
        title: "W",
        value: GranularityNames.WEEKLY,
        disabled: false,
    },
    {
        title: "M",
        value: GranularityNames.MONTHLY,
        disabled: false,
    },
];

export const getDurationSwitcherOptions = (duration: string): ISwitcherItem[] => [
    duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS
        ? {
              title: "28D",
              value: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
              disabled: false,
          }
        : {
              title: "1M",
              value: BasicDurations.LAST_MONTH,
              disabled: false,
          },
    {
        title: "3M",
        value: BasicDurations.LAST_THREE_MONTHS,
        disabled: false,
    },
    {
        title: "6M",
        value: BasicDurations.LAST_SIX_MONTHS,
        disabled: isDurationSwitcherDisabled(BasicDurations.LAST_SIX_MONTHS),
        isLocked: isDurationSwitcherLocked(BasicDurations.LAST_SIX_MONTHS),
    },
    {
        title: "13M",
        value: BasicDurations.LAST_THIRTEEN_MONTHS,
        disabled: isDurationSwitcherDisabled(BasicDurations.LAST_THIRTEEN_MONTHS),
        isLocked: isDurationSwitcherLocked(BasicDurations.LAST_THIRTEEN_MONTHS),
    },
];

export const baseMetricItemsConfig = (showSourceTypeBreakdown: boolean): IBaseMetricItem[] => {
    const baseItems: IBaseMetricItem[] = [
        {
            name: "keyword.analysis.overview.page.v2.trend_over_time.volume",
            id: MetricNames.VOLUME,
            color: showSourceTypeBreakdown ? colorsPalettes.carbon[200] : CHART_COLORS.main[0],
            visible: true,
            type: chartTypes.LINE,
            zIndex: 2,
        },
    ];

    const clicksItems: IBaseMetricItem[] = showSourceTypeBreakdown
        ? [
              {
                  name: "keyword.analysis.overview.page.v2.trend_over_time.clicks.organic",
                  id: MetricNames.ORGANIC,
                  color: CHART_COLORS.sourceType.organic,
                  visible: true,
                  type: chartTypes.AREA,
              },
              {
                  name: "keyword.analysis.overview.page.v2.trend_over_time.clicks.paid",
                  id: MetricNames.PAID,
                  color: CHART_COLORS.sourceType.paid,
                  visible: true,
                  type: chartTypes.AREA,
              },
          ]
        : [
              {
                  name: "keyword.analysis.overview.page.v2.trend_over_time.clicks",
                  id: MetricNames.CLICKS,
                  color: CHART_COLORS.main[1],
                  visible: true,
                  type: chartTypes.LINE,
              },
          ];

    return [...baseItems, ...clicksItems].map((item) => ({
        ...item,
        customLegendComponent: LegendWithValue,
    }));
};

export const WIDGET_TITLE_KEY = "keyword.analysis.overview.page.v2.trend_over_time.widgetTitle";
export const WIDGET_TOOLTIP_KEY = "keyword.analysis.overview.page.v2.trend_over_time.tooltip";
export const GRAPH_API_URL = "api/KeywordAnalysis/Overview/VolumeClicksTrend";
export const EXCEL_URL = `${GRAPH_API_URL}/Excel`;
export const DURATION_URL_PARAM_KEY = "graphDuration";
export const GRANULARITY_URL_PARAM_KEY = "graphGranularity";

export const legendIdToTextMap = {
    Clicks: "keyword.analysis.overview.page.trend_over_time.legend.clicks.tooltip",
    Volume: "keyword.analysis.overview.page.trend_over_time.legend.volume.tooltip",
    Organic: "keyword.analysis.overview.page.trend_over_time.legend.clicks.organic.tooltip",
    Paid: "keyword.analysis.overview.page.trend_over_time.legend.clicks.paid.tooltip",
};
